import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Tabs,
  Tab,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Security,
  Notifications,
  Storage,
  Info,
  Delete,
  Download,
  Refresh,
  Save,
  Warning,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService, ApiJob } from '../services/api';

export function SettingsPage() {
  const [tab, setTab] = useState<'app' | 'apis'>('app');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const [debugModeEnabled, setDebugModeEnabled] = useState(false);
  const [openClearCacheDialog, setOpenClearCacheDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleClearCache = () => {
    // TODO: Implement cache clearing
    toast.success('Cache limpo com sucesso!');
    setOpenClearCacheDialog(false);
  };

  const handleExportData = (data: any) => {
    // TODO: Implement data export
    console.log('Export data:', data);
    toast.success('Dados exportados com sucesso!');
    setOpenExportDialog(false);
  };

  const handleResetSettings = () => {
    // TODO: Implement settings reset
    toast.success('Configurações resetadas para o padrão!');
  };

  const { data: apiJobs, isLoading: isLoadingJobs } = useQuery(
    ['apiJobs'],
    () => apiService.listApiJobs(),
    { enabled: tab === 'apis' }
  );

  const toggleJob = useMutation({
    mutationFn: ({ key, isActive }: { key: string; isActive: boolean }) =>
      apiService.toggleApiJob(key, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiJobs'] });
      toast.success('Atualizado!');
    },
  });

  const runJobNow = useMutation({
    mutationFn: (key: string) => apiService.runApiJobNow(key),
    onSuccess: () => {
      toast.success('Disparo enviado!');
      queryClient.invalidateQueries({ queryKey: ['apiJobs'] });
    },
  });

  const scheduleLabel = useMemo(() => {
    return (j: ApiJob) => {
      if (j.schedule === 'manual') return 'Manual (somente ao inserir arquivo‑chave)';
      if (j.schedule === 'daily') return 'Diário (1x/dia)';
      if (j.schedule === 'hourly') return 'Horário';
      return j.schedule;
    };
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Configurações
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab value="app" label="App" />
        <Tab value="apis" label="APIs" />
      </Tabs>

      <Grid container spacing={3}>
        {/* App Settings */}
        {tab === 'app' && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Settings sx={{ mr: 1 }} />
                Configurações do App
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText
                    primary="Notificações Push"
                    secondary="Receber notificações sobre mudanças importantes"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationsEnabled}
                      onChange={(e) => setNotificationsEnabled(e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Refresh />
                  </ListItemIcon>
                  <ListItemText
                    primary="Atualização Automática"
                    secondary="Atualizar dados automaticamente em segundo plano"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={autoRefreshEnabled}
                      onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText
                    primary="Modo Escuro"
                    secondary="Usar tema escuro para economizar bateria"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={darkModeEnabled}
                      onChange={(e) => setDarkModeEnabled(e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText
                    primary="Modo Debug"
                    secondary="Ativar logs detalhados para desenvolvimento"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={debugModeEnabled}
                      onChange={(e) => setDebugModeEnabled(e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Data Management */}
        {tab === 'app' && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Storage sx={{ mr: 1 }} />
                Gerenciamento de Dados
              </Typography>
              
              <List>
                <ListItem button onClick={() => setOpenClearCacheDialog(true)}>
                  <ListItemIcon>
                    <Delete />
                  </ListItemIcon>
                  <ListItemText
                    primary="Limpar Cache"
                    secondary="Remover dados temporários para liberar espaço"
                  />
                </ListItem>

                <ListItem button onClick={() => setOpenExportDialog(true)}>
                  <ListItemIcon>
                    <Download />
                  </ListItemIcon>
                  <ListItemText
                    primary="Exportar Dados"
                    secondary="Baixar todos os dados em formato CSV/JSON"
                  />
                </ListItem>

                <ListItem button onClick={handleResetSettings}>
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText
                    primary="Resetar Configurações"
                    secondary="Voltar às configurações padrão"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* System Information */}
        {tab === 'app' && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1 }} />
                Informações do Sistema
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Versão do App"
                    secondary="1.0.0"
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Última Atualização"
                    secondary="Há 2 horas"
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Status da API"
                    secondary={
                      <Chip
                        label="Conectado"
                        color="success"
                        size="small"
                        icon={<Info />}
                      />
                    }
                  />
                </ListItem>

              </List>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Security */}
        {tab === 'app' && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <Security sx={{ mr: 1 }} />
                Segurança
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Suas credenciais são criptografadas e armazenadas de forma segura.
                </Typography>
              </Alert>

              <List>
                <ListItem>
                  <ListItemText
                    primary="Sessão Ativa"
                    secondary="Sua sessão expira em 24 horas"
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Último Login"
                    secondary={new Date().toLocaleString('pt-BR')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Criptografia"
                    secondary="TLS 1.3 ativo"
                  />
                </ListItem>
              </List>

              <Button
                variant="outlined"
                color="error"
                fullWidth
                sx={{ mt: 2 }}
                startIcon={<Security />}
              >
                Alterar Senha
              </Button>
            </CardContent>
          </Card>
        </Grid>
        )}

        {tab === 'apis' && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  APIs e Rotinas (Cron)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Lista de rotinas persistentes do sistema. Cada rotina lê/atualiza dados no banco e roda na periodicidade configurada.
                </Typography>

                {isLoadingJobs ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Carregando…</Typography>
                  </Stack>
                ) : (
                  <Grid container spacing={2}>
                    {(apiJobs ?? []).map((job) => (
                      <Grid item xs={12} md={6} key={job.key}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                              <Box>
                                <Typography variant="subtitle1" fontWeight={700}>{job.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{job.key}</Typography>
                              </Box>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={!!job.is_active}
                                    onChange={(_, checked) => toggleJob.mutate({ key: job.key, isActive: checked })}
                                  />
                                }
                                label={job.is_active ? 'Ativa' : 'Inativa'}
                              />
                            </Stack>

                            {job.description && (
                              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                                {job.description}
                              </Typography>
                            )}

                            <Divider sx={{ my: 1.5 }} />

                            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={() => runJobNow.mutate(job.key)}
                                disabled={runJobNow.isPending}
                              >
                                Executar agora
                              </Button>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                              <Chip label={`Periodicidade: ${scheduleLabel(job)}`} size="small" variant="outlined" />
                              <Chip
                                label={job.last_status ? `Último status: ${job.last_status}` : 'Sem execuções'}
                                size="small"
                                color={job.last_status === 'success' ? 'success' : job.last_status === 'failed' ? 'error' : 'default'}
                                variant="outlined"
                              />
                              {job.last_run_at && (
                                <Chip
                                  label={`Última execução: ${new Date(job.last_run_at).toLocaleString('pt-BR')}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Stack>

                            {job.last_message && (
                              <Alert severity="info" sx={{ mt: 1.5 }}>
                                <Typography variant="body2">{job.last_message}</Typography>
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Clear Cache Dialog */}
      <Dialog open={openClearCacheDialog} onClose={() => setOpenClearCacheDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Warning color="warning" sx={{ mr: 1 }} />
            Limpar Cache
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja limpar o cache? Isso pode afetar a performance temporariamente
            enquanto os dados são recarregados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClearCacheDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={handleClearCache} color="warning" variant="contained">
            Limpar Cache
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Data Dialog */}
      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Download sx={{ mr: 1 }} />
            Exportar Dados
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(handleExportData)}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Escolha o formato e período para exportação dos dados.
            </Typography>
            
            <TextField
              fullWidth
              select
              label="Formato"
              margin="normal"
              defaultValue="csv"
              {...register('format', { required: 'Formato é obrigatório' })}
              error={!!errors.format}
              helperText={errors.format?.message as string}
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="xlsx">Excel</option>
            </TextField>

            <TextField
              fullWidth
              select
              label="Período"
              margin="normal"
              defaultValue="all"
              {...register('period', { required: 'Período é obrigatório' })}
              error={!!errors.period}
              helperText={errors.period?.message as string}
            >
              <option value="all">Todos os dados</option>
              <option value="last_month">Último mês</option>
              <option value="last_3_months">Últimos 3 meses</option>
              <option value="last_year">Último ano</option>
            </TextField>

            <TextField
              fullWidth
              select
              label="Tipo de Dados"
              margin="normal"
              defaultValue="changes"
              {...register('dataType', { required: 'Tipo de dados é obrigatório' })}
              error={!!errors.dataType}
              helperText={errors.dataType?.message as string}
            >
              <option value="changes">Mudanças Legislativas</option>
              <option value="legislation">Legislação</option>
              <option value="logs">Logs de Envio</option>
              <option value="all">Todos os dados</option>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenExportDialog(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" startIcon={<Download />}>
              Exportar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
